/* eslint-disable no-unused-vars */
import './style.scss';

import PropTypes from 'prop-types';
import ReactRouterPrompt from 'react-router-prompt';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { Button } from 'primereact/button';
import { classNames } from 'primereact/utils';
import moment from 'moment';
import {
  DatePicker,
  CurrencyInput,
  TextInput,
  NumberInput,
  CustomFileInput,
} from 'components/FormControl';
import { toast } from 'layout';
import { downloadFile, createContract, downloadDocxByTemplateFile } from 'apis/projects.api';
import { formatFileName, convertUnicodeToAscii } from 'utils/func';
import Loading from 'components/Loading';
import { TOAST } from 'constant';
import UploadAppendixFiles from '../UploadAppendixFiles';

function GeneralInfoForm({
  errors,
  control,
  footerConfirmationPrompt,
  isDirty,
  dirtyFields,
  watch,
  setValue,
  getValues,
  disabled,
  totalExpenditurePP,
  projectType,
  // data,
}) {
  // #region Data
  const { t } = useTranslation();
  const { mutate: mutateDowloadFile } = useMutation(downloadFile);
  const currencyOptions = {
    style: 'currency',
    currency: 'VND',
  };

  const { projectProposalId } = useParams();
  const [totalExpenditure, setTotalExpenditure] = useState(0);
  const [appendixFilesDelete, setAppendixFilesDelete] = useState([]);
  const [appendixeArray, setAppendixeArray] = useState([]);

  const { mutate: mutateUpdateContract } = useMutation(createContract);

  // #endregion

  const expenditureFromVNUWatch = watch('expenditureFromVNU');
  const flexibleExpenditureWatch = watch('flexibleExpenditure');
  const expenditureFromFundingWatch = watch('expenditureFromFunding');

  // #region Event

  useEffect(() => {
    if (flexibleExpenditureWatch <= expenditureFromVNUWatch) {
      setValue('fixedExpenditure', expenditureFromVNUWatch - flexibleExpenditureWatch);
    }
    if (!disabled && flexibleExpenditureWatch > expenditureFromVNUWatch) {
      setValue('fixedExpenditure', expenditureFromVNUWatch);
      setTimeout(() => {
        setValue('flexibleExpenditure', 0);
      }, 1);
      toast('error', t('formControl.validation.canNotGreaterThanExpenditureFromVNU'));
    }
  }, [expenditureFromVNUWatch, flexibleExpenditureWatch]);

  useEffect(() => {
    if (!disabled && expenditureFromVNUWatch + expenditureFromFundingWatch > totalExpenditurePP) {
      if (expenditureFromVNUWatch > expenditureFromFundingWatch) {
        setTimeout(() => setValue('expenditureFromVNU', 0), 1);
      } else {
        setTimeout(() => setValue('expenditureFromFunding', 0), 1);
      }
      toast('error', t('formControl.validation.canNotGreaterThanTotalExpenditureProjectProposal'));
    } else {
      const ttExpenditure = expenditureFromVNUWatch + expenditureFromFundingWatch || 0;
      setValue('totalExpenditure', ttExpenditure);
      setTotalExpenditure(ttExpenditure);
    }
  }, [expenditureFromVNUWatch, expenditureFromFundingWatch]);
  // #endregion

  // #calculate numberMonthImplementation
  // Watch for changes in startDate and endDate
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  useEffect(() => {
    let numberMonthImplementationResult = 0;
    if (startDate && endDate) {
      numberMonthImplementationResult = Math.ceil(moment(endDate).diff(startDate, 'months', true));
      setValue('numberMonthImplementation', numberMonthImplementationResult);
    }
  }, [startDate, endDate]);

  const projectCode = String(watch('projectCode') ?? '');
  const fullNameOwner = watch('fullNameOwner');

  const {
    refetch: exportDocxFile,
    isLoading: isDownloadDocxFileLoading,
    // isFetching: isDownloadDocxFileFetching,
  } = useQuery(
    ['downloadProjectProposalDocxFile', projectProposalId],
    () => downloadDocxByTemplateFile(projectProposalId),
    {
      enabled: false,
      onSuccess: (res) => {
        if (res?.data) {
          const file = new Blob([res.data]);
          const filename = `${projectCode}-${fullNameOwner}-HopDong.docx`;
          const link = document.createElement('a');
          link.href = URL.createObjectURL(file);
          link.download = filename;

          link.click();
        }
      },
      onError: () => {
        toast('error', 'Lỗi tải xuống file!');
      },
    }
  );

  const handleDownloadFile = (path) => {
    mutateDowloadFile(
      { path },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const handleDownloadDocxFile = () => {
    const data = watch();
    const newData = { ...data };

    Object.keys(newData).forEach((key) => {
      if (key.toLowerCase().includes('expenditure')) {
        newData[key] = parseInt(newData[key], 10);
      }
    });

    const dataSender = {
      projectProposalId: projectProposalId ?? '0',
      ...newData,
    };

    delete dataSender?.appendixFiles;
    delete dataSender?.fileUrl;
    delete dataSender?.numberMonthImplementation;

    mutateUpdateContract(dataSender, {
      onSuccess: () => {
        exportDocxFile();
      },
      onError: () => {
        toast(TOAST.ERROR, t('notification.downloadFile.requireEnterInformation'));
      },
    });
  };

  const fileUrl = watch('fileUrl');
  const projectAppendixes = watch('projectAppendixes');

  const defaultNameFile = useMemo(
    () =>
      convertUnicodeToAscii(
        `${(projectCode ?? '').replace(/\//g, '-')}-${fullNameOwner}-HopDong.pdf`
      ),
    [projectCode, fullNameOwner]
  );

  useEffect(() => {
    setAppendixeArray(projectAppendixes ?? []);
    setValue('projectAppendixDeletedFileIds', []);
  }, [projectAppendixes]);

  useEffect(() => {
    setValue('projectAppendixDeletedFileIds', appendixFilesDelete ?? []);
  }, [appendixFilesDelete]);

  return (
    <div className="relative">
      <h4 className="text-center mb-4">
        {t('projectProposal.general.label')} {t('contract.name')}
      </h4>
      <div className="grid p-fluid form-table">
        <table className="mb-2 col-12">
          <thead>
            <tr>
              <th className="text-center" style={{ minWidth: 260, width: 360 }}>
                {t('projectProposal.expenditure.expenditure.fields.term')}
              </th>
              <th className="text-center" style={{ minWidth: 130, width: 360 }}>
                {t('projectProposal.expenditure.expenditure.fields.total')}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="font-bold">{t('contract.form.expenditureFromVNU')}</td>
              <td>
                <CurrencyInput
                  name="expenditureFromVNU"
                  control={control}
                  errors={errors}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr>
              <td>{t('contract.form.fixedExpenditure')}</td>
              <td>
                <CurrencyInput name="fixedExpenditure" control={control} errors={errors} readOnly />
              </td>
            </tr>
            <tr>
              <td>{t('contract.form.flexibleExpenditure')}</td>
              <td>
                <CurrencyInput
                  name="flexibleExpenditure"
                  control={control}
                  errors={errors}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr>
              <td className="font-bold">{t('contract.form.expenditureFromFunding')}</td>
              <td>
                <CurrencyInput
                  name="expenditureFromFunding"
                  control={control}
                  errors={errors}
                  readOnly={disabled}
                />
              </td>
            </tr>
            <tr>
              <td className="font-bold">{t('contract.form.totalExpenditure')}</td>
              <td className="text-right p-3 font-bold">
                <input type="number" name="totalExpenditure" hidden value={totalExpenditure} />
                {getValues('totalExpenditure')?.toLocaleString('vi', currencyOptions)}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="col-4 my-2">
          <DatePicker
            label={t('contract.form.startDate')}
            name="startDate"
            control={control}
            errors={errors}
            isRequired
            readOnly={disabled}
          />
        </div>
        <div className="col-4 my-2">
          <DatePicker
            label={t('contract.form.endDate')}
            name="endDate"
            control={control}
            errors={errors}
            isRequired
            readOnly={disabled}
          />
        </div>
        <div className="col-4 my-2">
          <NumberInput
            label={t('contract.form.numberMonthImplementation')}
            name="numberMonthImplementation"
            defaultValue={0}
            control={control}
            errors={errors}
            readOnly
          />
        </div>

        <div className="col-4 my-2">
          <TextInput
            label={t('contract.form.code')}
            name="projectCode"
            control={control}
            errors={errors}
            isRequired
            readOnly={disabled}
          />
        </div>
        <div className="col-4 my-2">
          <TextInput
            label={t('contract.form.topicCode')}
            name="topicCode"
            control={control}
            errors={errors}
            isRequired
            readOnly={disabled}
          />
        </div>
        <div className="col-4 my-2">
          <DatePicker
            label={t('contract.form.signingDate')}
            name="signingDate"
            control={control}
            errors={errors}
            readOnly={disabled}
          />
        </div>
        {!disabled && (
          <div className="col-12 my-2 flex align-items-center gap-2">
            <label htmlFor="" className="block" style={{ fontWeight: 'bold' }}>
              {t('contract.form.downloadTemplate')} (&nbsp;
              <a
                className="text-blue-500 font-italic text-lg font-medium"
                href={projectType === 'C' ? '/files/HopDongC.docx' : '/files/HopDong.docx'}
                download
              >
                {t('contract.form.dowloadTemplateContract')}{' '}
                <i className="pi pi-download text-blue-500" />
              </a>
              &nbsp;):
            </label>
            <Button
              type="button"
              label={t('contract.form.download')}
              className="w-auto py-2"
              icon={classNames(
                'pi',
                { 'pi-spin pi-spinner': isDownloadDocxFileLoading },
                { 'pi pi-download': !isDownloadDocxFileLoading }
              )}
              severity="info"
              onClick={handleDownloadDocxFile}
            />
          </div>
        )}

        <div className="col-12">
          {!disabled && (
            <CustomFileInput
              name="fileUrl"
              label={t('contract.form.contractUpload')}
              accept=".pdf"
              acceptOnly="pdfOnly"
              acceptFileMessage="Chỉ chấp nhận file pdf"
              defaultNameFile={defaultNameFile}
              control={control}
              errors={errors}
              isRequired
            />
          )}
          {fileUrl && (
            <div className="flex align-items-center gap-2">
              {disabled && (
                <h5 className="font-semibold text-xl inline-block mb-0">File hợp đồng: </h5>
              )}
              <span
                className={`underline  ${disabled ? 'text-lg' : ''}`}
                style={{ color: 'blue', cursor: 'pointer' }}
                onClick={() => handleDownloadFile(fileUrl)}
              >
                {formatFileName(typeof fileUrl === 'string' ? fileUrl : fileUrl?.name)}
              </span>
            </div>
          )}
        </div>

        <div className="col-12">
          <UploadAppendixFiles
            control={control}
            errors={errors}
            data={{ id: projectCode, name: 'PhuLuc' }}
            appendixeArray={appendixeArray}
            handleDownloadFile={handleDownloadFile}
            setAppendixFilesDelete={setAppendixFilesDelete}
            setAppendixeArray={setAppendixeArray}
            disabled={disabled}
          />
        </div>
      </div>

      {isDownloadDocxFileLoading && <Loading />}

      {!disabled && (
        <ReactRouterPrompt when={isDirty && Object.keys(dirtyFields)}>
          {({ isActive, onConfirm, onCancel }) => (
            <Dialog
              visible={isActive}
              header={<p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>}
              position="center"
              style={{ width: '300px' }}
              onHide={onCancel}
              draggable={false}
              footer={() => footerConfirmationPrompt(onConfirm, onCancel)}
            >
              <p className="text-lg py-3">{t('projectProposal.confirmationPromptMessage')}</p>
            </Dialog>
          )}
        </ReactRouterPrompt>
      )}
    </div>
  );
}

GeneralInfoForm.propTypes = {
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
  footerConfirmationPrompt: PropTypes.func,
  // eslint-disable-next-line react/forbid-prop-types
  dirtyFields: PropTypes.object,
  isDirty: PropTypes.bool,
  disabled: PropTypes.bool,
  watch: PropTypes.func.isRequired,
  setValue: PropTypes.func.isRequired,
  getValues: PropTypes.func.isRequired,
  totalExpenditurePP: PropTypes.number,
  projectType: PropTypes.string,
};

GeneralInfoForm.defaultProps = {
  dirtyFields: {},
  isDirty: false,
  disabled: false,
  footerConfirmationPrompt: () => null,
  totalExpenditurePP: 0,
  projectType: '',
};

export default GeneralInfoForm;
